import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../SEO";
import config from "../../config";
import api, { baseURL } from "../../utils/api";
import { useTheSeaContext } from "./TheSeaContext";

export default function PointPurchase() {
  const navigate = useNavigate();
  const { isLoggedIn, pointsBalance, setPointsBalance } = useTheSeaContext();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyingPackageId, setBuyingPackageId] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/loginsignup", { state: { returnTo: "/points", mode: "login" } });
      return;
    }

    const load = async () => {
      try {
        const [packagesRes, balanceRes] = await Promise.all([
          api.get(`${baseURL}/anonymous/point/packages`),
          api.get(`${baseURL}/authenticated/point/balance`),
        ]);
        setPackages(packagesRes.data?.packages || []);
        setPointsBalance(balanceRes.data?.balance ?? 0);
      } catch (err) {
        console.error("Failed to load point packages:", err);
        setError("Couldn't load point packages. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuy = async (packageId) => {
    setError("");
    setBuyingPackageId(packageId);
    try {
      const { data } = await api.post(`${baseURL}/authenticated/point/create_checkout_session`, { packageId });
      if (!data.url) throw new Error("Missing Stripe Checkout URL from backend");
      window.location.href = data.url;
    } catch (err) {
      console.error("Error during redirect to Stripe:", err);
      setError("Failed to start checkout. Please try again.");
      setBuyingPackageId(null);
    }
  };

  return (
    <>
      <SEO
        title="Buy Points — askTheSea"
        description="Buy points to keep using askTheSea's regulatory intelligence agent."
        url={`${config.BaseUrl}/points`}
        image={`${config.BaseUrl}/assets/logo.png?v=2`}
        noindex
      />
      <section className="page active" id="page-points">
        <div className="wrap about-hero">
          <div className="eyebrow">Points</div>
          <h1>Buy points</h1>
          <p className="lede">Each question costs one point. Buy a package below to keep the conversation going.</p>
        </div>

        <div className="wrap">
          <div className="balance-banner">
            <span className="balance-label">Current balance</span>
            <span className="balance-value">{pointsBalance} point{pointsBalance === 1 ? "" : "s"}</span>
          </div>

          {error && <p style={{ color: "#c0392b", marginBottom: "20px" }}>{error}</p>}

          {loading ? (
            <p>Loading packages…</p>
          ) : (
            <div className="package-grid">
              {packages.map((pkg) => (
                <div className="package-card" key={pkg.packageId}>
                  <span className="package-points">{pkg.points} points</span>
                  <span className="package-price">${pkg.price}</span>
                  <p>{pkg.description}</p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={buyingPackageId !== null}
                    onClick={() => handleBuy(pkg.packageId)}
                  >
                    {buyingPackageId === pkg.packageId ? "Redirecting to Stripe…" : "Buy"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
